import { DateTime } from "luxon";

// return a Luxon DateTime object
const toLuxonDate = date => {
  return DateTime.fromJSDate(date ? new Date(date) : new Date());
};

/** Function that displays the data and the time in the right format
 * @param date - The date that needs formatting
 * @param formatDate - The format the date needs to appear
 * @param timeZone - The timezone in which the time needs to appear
 */
const dateFormat = (date, formatDate, timeZone, defaultTimeZone) => {
  //defaultTimeZone: If date is in different format other than iso, we can give the info in defaultTimezone (eg- utc date)
  // TODO: Ternary condition should removed and handle it in default param value
  let st = defaultTimeZone
    ? DateTime.fromISO(date, { setZone: true, zone: defaultTimeZone })
    : DateTime.fromISO(date, { setZone: true });
  if (timeZone) {
    st = st.setZone(timeZone);
  }
  return st.toFormat(formatDate) !== "Invalid DateTime" ? st.toFormat(formatDate) : date;
};

/** Function that displays the data and the time in the right format
 * @param date - The date that needs formatting
 * @param timeZone - The timezone in which the time needs to appear
 */
const timeZoneFormat = (date, timeZone) => {
  let st = DateTime.fromISO(date, { setZone: true });
  if (timeZone) {
    st = st.setZone(timeZone);
  }
  return st !== "Invalid DateTime" ? st : date;
};

/** Function that displays the data and the time in the right format
 * @param date - The date that needs formatting
 * @param timeZone - The timezone in which the time needs to appear
 */
const timeZoneCustomFormat = (date, timeZone, format) => {
  let st = DateTime.fromISO(date, { setZone: true });
  if (timeZone) {
    st = st.setZone(timeZone);
  }
  st !== "Invalid DateTime" ? st : date;
  return this.dateFormat(st, format);
};

/** Function that displays the data and the time to local system format
 * @param date - The date that needs formatting
 * @param formatDate - The format the date needs to appear
 */
const getLocalTimeZoneDate = (date, format) => {
  const localTime = DateTime.fromISO(date, {
    zone: "UTC"
  }).toLocal();
  let st = DateTime.fromISO(localTime, { setZone: true });
  return st.toFormat(format) !== "Invalid DateTime" ? st.toFormat(format) : date;
};

/** Function that retrieve the future date period from now/startDate based on the range in native JS date format
 * @param range - Number. If need 14 days, pass it as 14
 * @returns like Mon Mar 22 2021 18:30:40 GMT+0000 (Greenwich Mean Time)
 *
 * @alternative new Date(new Date().getTime() + 14 * 24 * 60 * 60 * 1000): 14 future days
 */
const getFutureDTInJSDateFormat = (days, startDate = "") => {
  if (startDate) {
    return toLuxonDate(startDate)
      .plus({ days: Number(days) })
      .toJSDate();
  } else {
    return DateTime.now()
      .plus({ days: Number(days) })
      .toJSDate();
  }
};

/** Function that retrieve the past date period from now/startDate based on the range in native JS date format
 * @param range - Number. If need 14 days, pass it as 14
 * @returns like Mon Mar 22 2021 18:30:40 GMT+0000 (Greenwich Mean Time)
 *
 * @alternative new Date(new Date().getTime() - 14 * 24 * 60 * 60 * 1000): 14 past days
 */
const getPastDTInJSDateFormat = (days, startDate = "") => {
  if (startDate) {
    return toLuxonDate(startDate)
      .minus({ days: Number(days) })
      .toJSDate();
  } else {
    return DateTime.now()
      .minus({ days: Number(days) })
      .toJSDate();
  }
};

/** Generates date in JS format (mainly for Vue-DatePicker)
 * @param date - Date
 * @returns return JS format date(Mon Mar 22 2021 18:30:40 GMT+0000 (Greenwich Mean Time))
 *
 */
const generateJSDateFormat = date => {
  const dateFragment = date.split("-"); // 0:2021, 1:01, 2:22
  return DateTime.utc(parseInt(dateFragment[0]), parseInt(dateFragment[1]), parseInt(dateFragment[2])).toJSDate();
};

const remainingTimer = date => {
  const remainingTimerObject = DateTime.fromISO(date, { zone: "utc" })
    .diffNow(["days", "hours", "minutes"])
    .toObject();
  return {
    parsedValue: remainingTimerObject,
    isOverdue: remainingTimerObject.days <= 0 && remainingTimerObject.hours < 2,
    parsedValueFormatted: {
      DAY: remainingTimerObject.days,
      HOUR: remainingTimerObject.hours < 0 ? remainingTimerObject.hours * -1 : remainingTimerObject.hours,
      MINTUE: Math.round(
        remainingTimerObject.minutes < 0 ? remainingTimerObject.minutes * -1 : remainingTimerObject.minutes
      )
    }
  };
};

export {
  toLuxonDate,
  dateFormat,
  timeZoneFormat,
  getLocalTimeZoneDate,
  timeZoneCustomFormat,
  getFutureDTInJSDateFormat,
  getPastDTInJSDateFormat,
  generateJSDateFormat,
  remainingTimer
};
